import MileageLog from "../../../classes/MileageLog";
import Expense from "../../../classes/Expense";
import { db } from "../../../boot/firebase";
import { geocode } from "../../../util/gmaps";
import {
  collection,
  deleteDoc,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";

const METERS_PER_MILE = 1609.344;
const EARTH_RADIUS_MILES = 3958.7613;

const buildMapLink = (origin, destination) => {
  return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
    origin
  )}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
};

const toLocationString = (location) => {
  if (typeof location === "string") {
    return location;
  }

  if (location && location.lat != null && location.lng != null) {
    return `${location.lat},${location.lng}`;
  }

  return location?.address || "";
};

const toRoutesWaypoint = (location) => {
  if (location && location.lat != null && location.lng != null) {
    return {
      location: {
        latLng: {
          latitude: Number(location.lat),
          longitude: Number(location.lng),
        },
      },
    };
  }

  const address = typeof location === "string" ? location : location?.address;
  return { address };
};

const parseDuration = (duration) => {
  if (!duration || typeof duration !== "string") {
    return null;
  }

  const seconds = Number(duration.replace("s", ""));
  if (!Number.isFinite(seconds)) {
    return null;
  }

  const minutes = Math.round(seconds / 60);
  if (minutes <= 0) {
    return "< 1 min";
  }
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

const roundMiles = (miles) => {
  return Math.round(miles * 10) / 10;
};

const toRadians = (degrees) => {
  return (degrees * Math.PI) / 180;
};

const haversineMiles = (origin, destination) => {
  const latDelta = toRadians(destination.lat - origin.lat);
  const lngDelta = toRadians(destination.lng - origin.lng);
  const originLat = toRadians(origin.lat);
  const destinationLat = toRadians(destination.lat);

  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(originLat) *
      Math.cos(destinationLat) *
      Math.sin(lngDelta / 2) *
      Math.sin(lngDelta / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
};

const resolveCoordinates = async (location) => {
  if (location && location.lat != null && location.lng != null) {
    return {
      lat: Number(location.lat),
      lng: Number(location.lng),
    };
  }

  const address = typeof location === "string" ? location : location?.address;
  if (!address) {
    return null;
  }

  const result = await geocode({ address });
  const point = result?.geometry?.location;
  if (!point) {
    return null;
  }

  const lat = typeof point.lat === "function" ? point.lat() : point.lat;
  const lng = typeof point.lng === "function" ? point.lng() : point.lng;

  if (lat == null || lng == null) {
    return null;
  }

  return {
    lat: Number(lat),
    lng: Number(lng),
  };
};

const calculateDistanceWithRoutesApi = async ({
  origin,
  destination,
  originStr,
  destinationStr,
}) => {
  const response = await fetch(
    "https://routes.googleapis.com/directions/v2:computeRoutes",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": process.env.apiKey,
        "X-Goog-FieldMask": "routes.distanceMeters,routes.duration",
      },
      body: JSON.stringify({
        origin: toRoutesWaypoint(origin),
        destination: toRoutesWaypoint(destination),
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_UNAWARE",
        languageCode: "en-US",
        units: "IMPERIAL",
      }),
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = data?.error?.message || response.statusText || "Unknown";
    console.error(`Routes API request failed: ${message}`);
    throw new Error("routeLookupFailed");
  }

  const route = data?.routes?.[0];
  if (!route || route.distanceMeters == null) {
    throw new Error("routeLookupFailed");
  }

  return {
    miles: roundMiles(route.distanceMeters / METERS_PER_MILE),
    mapLink: buildMapLink(originStr, destinationStr),
    duration: parseDuration(route.duration),
    startAddress: originStr,
    endAddress: destinationStr,
  };
};

const calculateDistanceEstimate = async ({
  origin,
  destination,
  originStr,
  destinationStr,
}) => {
  const [originCoordinates, destinationCoordinates] = await Promise.all([
    resolveCoordinates(origin),
    resolveCoordinates(destination),
  ]);

  if (!originCoordinates || !destinationCoordinates) {
    throw new Error("Unable to resolve route coordinates");
  }

  return {
    miles: roundMiles(
      haversineMiles(originCoordinates, destinationCoordinates)
    ),
    mapLink: buildMapLink(originStr, destinationStr),
    duration: null,
    startAddress: originStr,
    endAddress: destinationStr,
    estimated: true,
  };
};

export const GET_MILEAGE_LOG = ({ dispatch }, id) => {
  return dispatch("GET_DOC", {
    converter: MileageLog,
    path: MileageLog.getPath(id),
    stateKey: "mileageLogs",
    stateID: id,
    id,
  });
};

export const GET_MILEAGE_LOGS = (
  { dispatch },
  { where, limit, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: MileageLog,
    path: MileageLog.getPath(),
    stateKey: "mileageLogs",
    where,
    limit,
    pagination,
  });
};

export const LISTEN_MILEAGE_LOG = (
  { dispatch },
  { id, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: MileageLog,
    path: MileageLog.getPath(id),
    stateKey: "mileageLogs",
    id,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_MILEAGE_LOGS = (
  { dispatch },
  { where, pagination, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: MileageLog,
    path: MileageLog.getPath(),
    stateKey: "mileageLogs",
    where,
    pagination,
    next,
    error,
    unlisten,
  });
};

export const SAVE_MILEAGE_LOG = async ({ commit, state }, { id, data }) => {
  const now = new Date();
  const user = state.user;

  // Ensure month/year are set from date
  if (data.date) {
    const dateObj = data.date instanceof Date ? data.date : new Date(data.date);
    data.month = dateObj.getMonth() + 1;
    data.year = dateObj.getFullYear();
  }

  // Create a MileageLog instance
  const log = new MileageLog(id, data);

  if (!id) {
    // Creating new - generate ID
    log.data.created = now;
    log.data.createdBy = user.id;

    const ref = doc(collection(db, MileageLog.getPath()));
    log.id = ref.id;

    await setDoc(ref, log.toFirestore());
  } else {
    // Updating existing
    log.data.modified = now;
    log.data.modifiedBy = user.id;

    await setDoc(doc(db, MileageLog.getPath(id)), log.toFirestore());
  }

  // Update local state
  commit("SET", { stateKey: "mileageLogs", stateID: log.id, doc: log });

  return log;
};

export const DELETE_MILEAGE_LOG = async ({ commit }, id) => {
  await deleteDoc(doc(db, MileageLog.getPath(id)));
  commit("REMOVE", { stateKey: "mileageLogs", id });
};

/**
 * Get the last stop for a user on a specific date (for "continue from last stop" feature)
 */
export const GET_LAST_STOP_FOR_DATE = ({ state }, { userID, date }) => {
  const dateStr =
    date instanceof Date ? date.toISOString().split("T")[0] : date;

  const logsForDate = Object.values(state.mileageLogs)
    .filter((log) => {
      if (log.data.userID !== userID) return false;
      if (!log.data.date) return false;
      const logDateStr = log.data.date.toISOString().split("T")[0];
      return logDateStr === dateStr;
    })
    .sort((a, b) => {
      // Sort by created time to get the most recent entry
      const aTime = a.data.created ? a.data.created.getTime() : 0;
      const bTime = b.data.created ? b.data.created.getTime() : 0;
      return bTime - aTime;
    });

  if (logsForDate.length === 0) return null;

  const lastLog = logsForDate[0];

  // Return the end location if it exists (route mode), otherwise null
  if (lastLog.data.endLocation) {
    return lastLog.data.endLocation;
  }

  return null;
};

/**
 * Calculate distance between two locations using Routes API (new),
 * with a geocoded straight-line fallback when routing is unavailable.
 * Returns { miles: number, mapLink: string }
 */
export const CALCULATE_ROUTE_DISTANCE = async (_, { origin, destination }) => {
  const originStr = toLocationString(origin);
  const destinationStr = toLocationString(destination);

  if (!originStr || !destinationStr) {
    throw new Error("routeLocationsRequired");
  }

  try {
    return await calculateDistanceWithRoutesApi({
      origin,
      destination,
      originStr,
      destinationStr,
    });
  } catch (error) {
    console.warn("Routes API distance lookup failed, using estimate:", error);

    try {
      return await calculateDistanceEstimate({
        origin,
        destination,
        originStr,
        destinationStr,
      });
    } catch (fallbackError) {
      console.error("Error calculating route distance:", {
        error,
        fallbackError,
      });
      throw error;
    }
  }
};

/**
 * Submit all unpaid mileage entries for reimbursement.
 * Creates an expense and marks the mileage logs as "submitted".
 */
export const SUBMIT_MILEAGE_FOR_REIMBURSEMENT = async (
  { commit, dispatch, state },
  { logs, ratePerMile }
) => {
  const user = state.user;
  const now = new Date();

  // Calculate total miles and reimbursement amount
  const totalMiles = logs.reduce((sum, log) => sum + (log.data.miles || 0), 0);
  const amount = Math.round(totalMiles * ratePerMile * 100) / 100;
  const logIds = logs.map((log) => log.id);
  const summaryDescription = `Mileage reimbursement: ${totalMiles} miles @ $${ratePerMile}/mile`;
  const logDescriptions = logs
    .map((log) => (log.data.description || "").trim())
    .filter(Boolean);
  const expenseDescription = logDescriptions.length
    ? `${summaryDescription} - ${logDescriptions.join("; ")}`
    : summaryDescription;

  // The reimbursement covers trips on several days; date it by the most
  // recent trip (the last time a cost was incurred), not by the submission.
  const latestTripDate = logs.reduce((latest, log) => {
    const date = log.data.date;
    return date && (!latest || date > latest) ? date : latest;
  }, null);

  // Create the expense
  const expense = new Expense(null, {
    date: latestTripDate || now,
    description: expenseDescription,
    amount,
    category: "Mileage Reimbursement",
    mileageLogIds: logIds,
  });

  const savedExpense = await dispatch("SAVE_EXPENSE", expense);

  // Update all mileage logs to "submitted" with the expense ID
  const batch = writeBatch(db);
  for (const log of logs) {
    const ref = doc(db, MileageLog.getPath(log.id));
    batch.update(ref, {
      status: "submitted",
      expenseID: savedExpense.id,
      modified: now,
      modifiedBy: user.id,
    });
  }
  await batch.commit();

  // Update local state
  for (const log of logs) {
    const updated = new MileageLog(log.id, {
      ...log.data,
      status: "submitted",
      expenseID: savedExpense.id,
      modified: now,
      modifiedBy: user.id,
    });
    commit("SET", { stateKey: "mileageLogs", stateID: log.id, doc: updated });
  }

  return { expense: savedExpense, totalMiles, amount };
};
